import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, AlertCircle, Check, ArrowRight, Sparkles, Mail, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

// Google OAuth Configuration - DO NOT MODIFY
const GOOGLE_AUTH_URL = "https://auth.emergentagent.com";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, verifyEmail, resendVerification, pendingVerification, setPendingVerification } = useAuth();
  const [step, setStep] = useState(pendingVerification ? 2 : 1); // 1: register form, 2: verification
  const [formData, setFormData] = useState({
    email: pendingVerification?.email || '',
    password: '',
    confirmPassword: '',
    bedrijfsnaam: ''
  });
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  
  const inputRefs = useRef([]);

  // Handle countdown for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Check if there's pending verification on mount
  useEffect(() => {
    if (pendingVerification) {
      setStep(2);
      setFormData(prev => ({ ...prev, email: pendingVerification.email }));
    }
  }, [pendingVerification]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setVerificationCode(paste.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }

    if (formData.password.length < 8) {
      setError('Wachtwoord moet minimaal 8 tekens bevatten');
      return;
    }

    // Check password requirements
    if (!/[A-Z]/.test(formData.password)) {
      setError('Wachtwoord moet minimaal 1 hoofdletter bevatten');
      return;
    }
    if (!/[a-z]/.test(formData.password)) {
      setError('Wachtwoord moet minimaal 1 kleine letter bevatten');
      return;
    }
    if (!/[0-9]/.test(formData.password)) {
      setError('Wachtwoord moet minimaal 1 cijfer bevatten');
      return;
    }

    setLoading(true);

    try {
      const result = await register(formData.email, formData.password, formData.bedrijfsnaam);
      
      if (result.requires_verification) {
        setStep(2);
        setCountdown(60);
        toast.success('Verificatiecode verzonden naar je e-mail!');
      } else {
        // Old flow fallback
        sessionStorage.setItem('urenbuddy_new_registration', 'true');
        toast.success('Account aangemaakt!');
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Registratie mislukt';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Vul alle 6 cijfers in');
      return;
    }

    setLoading(true);

    try {
      await verifyEmail(formData.email, code);
      sessionStorage.setItem('urenbuddy_new_registration', 'true');
      toast.success('E-mail geverifieerd! Welkom bij Urenbuddy!');
      navigate('/dashboard');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Verificatie mislukt';
      setError(errorMsg);
      toast.error(errorMsg);
      // Clear code on error
      setVerificationCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    
    setResending(true);
    setError('');

    try {
      await resendVerification(formData.email);
      setCountdown(60);
      toast.success('Nieuwe code verzonden!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Kon geen nieuwe code versturen';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setResending(false);
    }
  };

  const handleGoogleRegister = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `${GOOGLE_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const benefits = [
    { title: 'Gratis starten', desc: '10 registraties per maand' },
    { title: 'Eenvoudige timer', desc: 'Uren bijhouden met één klik' },
    { title: 'Professionele facturen', desc: 'Direct versturen naar klanten' },
    { title: 'Kilometers bijhouden', desc: 'Voor je belastingaangifte' }
  ];

  const securityFeatures = [
    'SSL/HTTPS versleuteling',
    'Veilige wachtwoordopslag',
    '2-staps verificatie'
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -left-40 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-60 h-60 bg-cyan-400/10 rounded-full blur-2xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/25">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Urenbuddy</span>
          </Link>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 mb-6 w-fit">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-300">Gratis starten</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            Begin vandaag met je <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">administratie</span>
          </h1>
          
          <p className="text-slate-300 text-lg mb-10 max-w-md">
            Sluit je aan bij duizenden freelancers die hun uren, kilometers en facturen beheren met Urenbuddy.
          </p>

          {/* Benefits grid */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-slate-800/50 backdrop-blur rounded-xl p-4 border border-slate-700/50">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-cyan-400" />
                  </div>
                  <span className="font-medium text-white text-sm">{benefit.title}</span>
                </div>
                <p className="text-slate-400 text-xs ml-7">{benefit.desc}</p>
              </div>
            ))}
          </div>

          {/* Security badge */}
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-teal-400" />
              <span className="text-white font-medium text-sm">Beveiligd met</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {securityFeatures.map((feature, i) => (
                <span key={i} className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded">
                  {feature}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-slate-50 dark:bg-slate-900">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl font-bold text-foreground">Urenbuddy</span>
            </Link>
          </div>

          <Card className="border-0 shadow-xl bg-white dark:bg-slate-800">
            <CardContent className="p-8">
              {step === 1 ? (
                <>
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-foreground mb-2">Gratis registreren</h2>
                    <p className="text-muted-foreground">Maak je account aan in 30 seconden</p>
                  </div>

                  {/* Google Register Button */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 font-medium border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 mb-6"
                    onClick={handleGoogleRegister}
                    data-testid="google-register-btn"
                  >
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Registreren met Google
                  </Button>

                  <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200 dark:border-slate-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white dark:bg-slate-800 px-3 text-muted-foreground">of met e-mail</span>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                      <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl flex items-center gap-3" data-testid="register-error">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">{error}</span>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="bedrijfsnaam" className="text-foreground font-medium">Bedrijfsnaam <span className="text-muted-foreground font-normal">(optioneel)</span></Label>
                      <Input
                        id="bedrijfsnaam"
                        name="bedrijfsnaam"
                        placeholder="Jouw Bedrijf B.V."
                        value={formData.bedrijfsnaam}
                        onChange={handleChange}
                        data-testid="register-company-input"
                        className="h-12 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-foreground font-medium">E-mailadres</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="naam@bedrijf.nl"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="register-email-input"
                        className="h-12 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-foreground font-medium">Wachtwoord</Label>
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={handleChange}
                          required
                          data-testid="register-password-input"
                          className="h-12 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-foreground font-medium">Bevestig</Label>
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          placeholder="••••••••"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required
                          data-testid="register-confirm-password-input"
                          className="h-12 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                        />
                      </div>
                    </div>

                    {/* Password requirements hint */}
                    <div className="text-xs text-muted-foreground bg-slate-100 dark:bg-slate-700/50 rounded-lg p-3">
                      <p className="font-medium mb-1">Wachtwoord vereisten:</p>
                      <ul className="space-y-0.5">
                        <li className={formData.password.length >= 8 ? 'text-teal-600' : ''}>
                          {formData.password.length >= 8 ? '✓' : '○'} Minimaal 8 tekens
                        </li>
                        <li className={/[A-Z]/.test(formData.password) ? 'text-teal-600' : ''}>
                          {/[A-Z]/.test(formData.password) ? '✓' : '○'} Minimaal 1 hoofdletter
                        </li>
                        <li className={/[a-z]/.test(formData.password) ? 'text-teal-600' : ''}>
                          {/[a-z]/.test(formData.password) ? '✓' : '○'} Minimaal 1 kleine letter
                        </li>
                        <li className={/[0-9]/.test(formData.password) ? 'text-teal-600' : ''}>
                          {/[0-9]/.test(formData.password) ? '✓' : '○'} Minimaal 1 cijfer
                        </li>
                      </ul>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-medium rounded-xl shadow-lg shadow-cyan-500/25 transition-all mt-2"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Account aanmaken...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Gratis Registreren
                          <ArrowRight className="w-5 h-5" />
                        </span>
                      )}
                    </Button>
                  </form>
                </>
              ) : (
                <>
                  {/* Email Verification Step */}
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/25">
                      <Mail className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">Verifieer je e-mail</h2>
                    <p className="text-muted-foreground">
                      We hebben een 6-cijferige code gestuurd naar<br />
                      <span className="font-medium text-foreground">{formData.email}</span>
                    </p>
                  </div>

                  <form onSubmit={handleVerify} className="space-y-6">
                    {error && (
                      <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl flex items-center gap-3" data-testid="verify-error">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">{error}</span>
                      </div>
                    )}

                    {/* 6-digit code input */}
                    <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                      {verificationCode.map((digit, index) => (
                        <Input
                          key={index}
                          ref={el => inputRefs.current[index] = el}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleCodeChange(index, e.target.value)}
                          onKeyDown={(e) => handleCodeKeyDown(index, e)}
                          className="w-12 h-14 text-center text-2xl font-bold bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                          data-testid={`verify-code-${index}`}
                        />
                      ))}
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-medium rounded-xl shadow-lg shadow-cyan-500/25 transition-all"
                      disabled={loading || verificationCode.join('').length !== 6}
                      data-testid="verify-submit-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Verifiëren...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Check className="w-5 h-5" />
                          E-mail Verifiëren
                        </span>
                      )}
                    </Button>

                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-2">
                        Geen code ontvangen?
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleResend}
                        disabled={countdown > 0 || resending}
                        className="text-cyan-600 hover:text-cyan-700 dark:text-cyan-400"
                        data-testid="resend-code-btn"
                      >
                        {resending ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        {countdown > 0 ? `Opnieuw versturen (${countdown}s)` : 'Verstuur nieuwe code'}
                      </Button>
                    </div>

                    <div className="text-center pt-4 border-t border-slate-200 dark:border-slate-700">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setStep(1);
                          setPendingVerification(null);
                          setVerificationCode(['', '', '', '', '', '']);
                          setError('');
                        }}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        ← Terug naar registratie
                      </Button>
                    </div>
                  </form>
                </>
              )}

              {step === 1 && (
                <div className="mt-6 text-center">
                  <span className="text-muted-foreground">Al een account? </span>
                  <Link 
                    to="/login" 
                    className="text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 font-semibold" 
                    data-testid="login-link"
                  >
                    Log hier in
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-8">
            Door te registreren ga je akkoord met onze{' '}
            <Link to="/voorwaarden" className="text-cyan-600 hover:underline">voorwaarden</Link>
            {' '}en{' '}
            <Link to="/privacy" className="text-cyan-600 hover:underline">privacybeleid</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
